/* Changing what a position is called.
 *
 * The ticker is not a label on a position — it is the key eight other kinds of
 * record are joined to it by. A thesis, a size ceiling, theme membership, every
 * trade, every dividend, a watchlist row and the trace of a breach all find
 * their position by matching this string. Editing it in one place and not the
 * others would not throw an error; it would quietly split one position into
 * two, one holding the shares and the other holding everything you had written
 * about them.
 *
 * So it is a rename across the whole book, done in one pass, or it is nothing.
 *
 * Kept pure and given the book to work on rather than the database, so the
 * result can be checked in a test rather than after the fact on a screen. What
 * it does not touch: `Holding.yahooSymbol`, which is a different fact — what
 * the exchange calls the listing, not what you call the position — and is
 * edited on its own. A company that renamed itself usually changed both, and
 * they are still two separate corrections because they can and do move apart. */

import type { BookData } from '../data/db';

export interface RenameCount {
  holdings: number;
  theses: number;
  caps: number;
  themes: number;
  trades: number;
  dividends: number;
  watchlist: number;
  alertLog: number;
  transfers: number;
  /** Every record the rename touched. */
  total: number;
}

export interface RenameOutcome {
  data: BookData;
  moved: RenameCount;
  /** True when something already sits under the new name and the rename would
      merge the two rather than move one. Not refused — merging two rows for the
      same company is a legitimate reason to do this — but the caller has to say
      so before it happens. */
  merges: boolean;
}

const norm = (t: string) => t.trim().toUpperCase();

/** Null when the rename is not one: a blank name, or the same name back. */
export function renameTicker(data: BookData, fromRaw: string, toRaw: string): RenameOutcome | null {
  const from = norm(fromRaw);
  const to = norm(toRaw);
  if (from === '' || to === '' || from === to) return null;

  const moved: RenameCount = {
    holdings: 0, theses: 0, caps: 0, themes: 0,
    trades: 0, dividends: 0, watchlist: 0, alertLog: 0, transfers: 0, total: 0,
  };

  const hit = (key: keyof Omit<RenameCount, 'total'>) => { moved[key] += 1; moved.total += 1; };

  const merges = [
    ...data.holdings, ...data.trades, ...data.dividends, ...data.watchlist,
    ...(data.transfers ?? []),
  ].some((r) => norm(r.ticker) === to)
    || data.theses.some((t) => norm(t.ticker) === to)
    || data.caps.some((c) => norm(c.ticker) === to);

  const data2: BookData = {
    ...data,

    holdings: data.holdings.map((h) =>
      norm(h.ticker) === from ? (hit('holdings'), { ...h, ticker: to }) : h),

    theses: data.theses.map((t) =>
      norm(t.ticker) === from ? (hit('theses'), { ...t, ticker: to }) : t),

    /* One ceiling per ticker. If both names already carry one the surviving
       record is the one being renamed onto — the user is saying these are the
       same position, and the name they are keeping is the one they typed. */
    caps: (() => {
      const renamed = data.caps.map((c) =>
        norm(c.ticker) === from ? (hit('caps'), { ...c, capKind: c.capKind, ticker: to }) : c);
      const seen = new Set<string>();
      return renamed.filter((c) => {
        const key = norm(c.ticker);
        if (key !== to) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })(),

    themes: data.themes.map((theme) => {
      if (!theme.tickers.some((t) => norm(t) === from)) return theme;
      hit('themes');
      // A position cannot be in a theme twice, so a rename onto a name already
      // in the same theme collapses to one entry rather than doubling it.
      const next = theme.tickers.map((t) => (norm(t) === from ? to : t));
      return { ...theme, tickers: [...new Set(next.map(norm))] };
    }),

    trades: data.trades.map((t) =>
      norm(t.ticker) === from ? (hit('trades'), { ...t, ticker: to }) : t),

    dividends: data.dividends.map((d) =>
      norm(d.ticker) === from ? (hit('dividends'), { ...d, ticker: to }) : d),

    watchlist: data.watchlist.map((w) =>
      norm(w.ticker) === from ? (hit('watchlist'), { ...w, ticker: to }) : w),

    /* The trace of a past breach keeps its own headline verbatim — it is a
       record of what the app said at the time, and rewriting it would make the
       history claim something that was never on screen. Only the join key
       moves, so the episode still finds its position. */
    alertLog: data.alertLog.map((e) =>
      e.ticker && norm(e.ticker) === from ? (hit('alertLog'), { ...e, ticker: to }) : e),

    /* A ninth join key, and the newest. Left out of this all-or-nothing pass it
       would orphan exactly the records the cost and tax arithmetic now depend
       on — `fifoRealised` reads transfers to know which account a parcel is in,
       and a transfer still naming the old ticker moves nothing, silently. */
    transfers: (data.transfers ?? []).map((x) =>
      norm(x.ticker) === from ? (hit('transfers'), { ...x, ticker: to }) : x),
  };

  return { data: data2, moved, merges };
}
